import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { useAuthChecker } from "../../Context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Download, SlidersHorizontal, RotateCcw, Wallet } from "lucide-react";
import { Spin, DatePicker, Input, Select, Drawer, Space, Button as AntdButton } from "antd";
import { FetchUsers_api } from "../../Api";
import { SERVER_API } from "../../Constant";
import axios from "axios";
import dayjs from "dayjs";
import { downloadExcel } from "../../lib/excelGenerator";

const { RangePicker } = DatePicker;

const toNumber = (value: any): number =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: "LKR",
    minimumFractionDigits: 2,
  }).format(value);

export const FinancialReports = () => {
  const { isAuthenticated, checkUserPermission, isLoading: permissionLoading, user } = useAuthChecker();
  const hasPermission =
    isAuthenticated &&
    (checkUserPermission("financial_reports") || checkUserPermission("sub_dealer_cash_report")) &&
    ["admin", "cashier", "manager"].includes(user?.userRole || "");

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [balance, setBalance] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [addedUserId, setAddedUserId] = useState<number | undefined>(undefined);
  const [slipId, setSlipId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [hasApplied, setHasApplied] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<any>(null);
  const [draftAddedUserId, setDraftAddedUserId] = useState<number | undefined>(undefined);
  const [draftSlipId, setDraftSlipId] = useState("");
  const [draftTypeFilter, setDraftTypeFilter] = useState("");
  const [draftMethodFilter, setDraftMethodFilter] = useState("");

  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    if (hasPermission) {
      FetchUsers_api(1, 1000, "").then((response: any) => {
        if (response && response.users) {
          setUsers(response.users);
        }
      });
    }
  }, [hasPermission]);

  const fetchReport = async (
    start: string,
    end: string,
    addedUser?: number,
    slip: string = "",
    side: string = "",
    method: string = "",
  ) => {
    if (!start || !end) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVER_API}/Reports/fetch_financial_reports.php`,
        {
          params: {
            type: "balance",
            startDate: start,
            endDate: end,
            addedUser,
            slipId: slip,
            side,
            method,
          },
        },
      );
      const res = response.data;
      if (res.success) {
        setReportData(res.data || []);
        setTotalReceived(Number(res.totalReceived) || 0);
        setTotalPaid(Number(res.totalPaid) || 0);
        setBalance(Number(res.balance) || 0);
      } else {
        alert(res.msg || "Failed to fetch report");
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftRange(startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null);
    setDraftAddedUserId(addedUserId);
    setDraftSlipId(slipId);
    setDraftTypeFilter(typeFilter);
    setDraftMethodFilter(methodFilter);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    const s = draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "";
    const e = draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "";
    setStartDate(s);
    setEndDate(e);
    setAddedUserId(draftAddedUserId);
    setSlipId(draftSlipId.trim());
    setTypeFilter(draftTypeFilter);
    setMethodFilter(draftMethodFilter);
    setHasApplied(true);
    setDrawerOpen(false);
    fetchReport(s, e, draftAddedUserId, draftSlipId.trim(), draftTypeFilter, draftMethodFilter);
  };

  const resetFilters = () => {
    setDraftRange(null);
    setDraftAddedUserId(undefined);
    setDraftSlipId("");
    setDraftTypeFilter("");
    setDraftMethodFilter("");
    setStartDate("");
    setEndDate("");
    setAddedUserId(undefined);
    setSlipId("");
    setTypeFilter("");
    setMethodFilter("");
    setReportData([]);
    setTotalReceived(0);
    setTotalPaid(0);
    setBalance(0);
    setHasApplied(false);
    setDrawerOpen(false);
  };

  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";
  const activeUserName = users.find((u) => u.id === addedUserId)?.username || "";
  const filterSummary = [
    periodLabel,
    typeFilter || "",
    methodFilter || "",
    activeUserName,
    slipId ? `Slip: ${slipId}` : "",
  ].filter(Boolean).join(" | ");

  const rowsWithBalance = (() => {
    let run = 0;
    return reportData.map((r) => {
      run += r.side === "Received" ? toNumber(r.amount) : -toNumber(r.amount);
      return { ...r, runningBalance: run };
    });
  })();

  const receivedCount = reportData.filter((r) => r.side === "Received").length;
  const paidCount = reportData.filter((r) => r.side === "Paid").length;

  const handleDownloadExcel = () => {
    const headers = [
      "Date",
      "Type",
      "Method",
      "Source",
      "Ref ID",
      "Added By",
      "Amount",
      "Running Balance",
    ];
    const data = rowsWithBalance.map((row) => [
      row.date,
      row.side,
      row.method,
      row.source,
      row.ref_id,
      row.added_by || "N/A",
      toNumber(row.amount),
      toNumber(row.runningBalance),
    ]);
    downloadExcel(
      `Cash Balance Report${filterSummary ? ` (${filterSummary})` : ""}`,
      headers,
      data,
      `Cash_Balance_Report_${startDate}_${endDate}`,
      formatCurrency(totalPaid),
      undefined,
      formatCurrency(totalReceived),
      `Net Balance: ${formatCurrency(balance)} | Received: ${receivedCount} | Paid: ${paidCount}`,
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Cash Received Balance Report</h1>
        <div className="flex gap-3">
          <Button
            onClick={openDrawer}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
          >
            <SlidersHorizontal className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Filters</span>
          </Button>
          <Button
            onClick={handleDownloadExcel}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Wallet className="w-4 h-4 text-emerald-600" />
          <span>Filters: </span>
          <span className="font-medium text-gray-700">
            {filterSummary || "None applied — open Filters to pick a period"}
          </span>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex flex-wrap gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL RECEIVED</p>
                <p className="text-2xl font-bold">{formatCurrency(totalReceived)}</p>
                <p className="text-[10px] opacity-70 mt-1">{receivedCount} transactions</p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL REMITTANCE PAID</p>
                <p className="text-2xl font-bold">{formatCurrency(totalPaid)}</p>
                <p className="text-[10px] opacity-70 mt-1">{paidCount} transactions</p>
              </div>
              <div className={balance < 0 ? "bg-red-600/40 rounded-lg px-4 py-2 -my-2" : ""}>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">NET BALANCE</p>
                <p className="text-2xl font-bold">{formatCurrency(balance)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Type</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Method</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Source</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Ref ID</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Added By</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Running Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rowsWithBalance.map((row, index) => (
                      <TableRow
                        key={`${row.ref_id}-${index}`}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <TableCell className="px-4 py-3">{row.date}</TableCell>
                        <TableCell className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${
                              row.side === "Received"
                                ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                                : "bg-red-50 text-red-600 border-red-200"
                            }`}
                          >
                            {row.side}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3 capitalize">{row.method}</TableCell>
                        <TableCell className="px-4 py-3">{row.source}</TableCell>
                        <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">{row.ref_id}</TableCell>
                        <TableCell className="px-4 py-3">{row.added_by || "N/A"}</TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">
                          {formatCurrency(toNumber(row.amount))}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right font-semibold">
                          {formatCurrency(toNumber(row.runningBalance))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {!loading && reportData.length === 0 && hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No cash movements recorded for the applied period.</p>
          </div>
        </div>
      )}
      {!loading && reportData.length === 0 && !hasApplied && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Open Filters and pick a period to view the cash balance statement.</p>
          </div>
        </div>
      )}

      <Drawer
        title="Cash Balance Report Filters"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton
              onClick={resetFilters}
              className="flex items-center gap-2 border-gray-200"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton
              type="primary"
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Apply Filters
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Added User
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Users"
              value={draftAddedUserId}
              onChange={(val: any) => setDraftAddedUserId(val)}
              allowClear
              showSearch
              optionFilterProp="children"
              options={users.map((u) => ({ value: u.id, label: u.username }))}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Type
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Types"
              value={draftTypeFilter || undefined}
              onChange={(val) => setDraftTypeFilter(val || "")}
              allowClear
              options={[
                { value: "Received", label: "Received" },
                { value: "Paid", label: "Paid" },
              ]}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Method
            </label>
            <Select
              className="w-full"
              size="large"
              placeholder="All Methods"
              value={draftMethodFilter || undefined}
              onChange={(val) => setDraftMethodFilter(val || "")}
              allowClear
              options={[
                { value: "Direct", label: "Direct" },
                { value: "Remittance", label: "Remittance" },
              ]}
            />
          </div>

          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Slip / Transfer ID
            </label>
            <Input
              size="large"
              placeholder="e.g. DC92484"
              value={draftSlipId}
              onChange={(e) => setDraftSlipId(e.target.value)}
              allowClear
            />
          </div>
        </Space>
      </Drawer>
    </div>
  );
};